import { useEffect, useId, useRef, useState } from "react"

import { Hand, LoaderCircle, Magnet, RotateCcw } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/shared/lib/utils"
import { WebApp } from "@/shared/telegram"

import { loadPuzzleImage } from "../lib/load-puzzle-image"
import {
  BOARD_SIZE,
  PIECE_CANVAS_SIZE,
  PIECE_START,
  PIECE_TAB,
  PUZZLE_COLORS,
  clampPiece,
  isWithinSnap,
  nudgePiece,
  pickPicture,
  pickTarget,
  piecePath,
  type PuzzlePoint
} from "../model/puzzle"

type PuzzlePhase = "loading" | "idle" | "dragging" | "near" | "miss" | "placed"

interface DragState {
  pointerId: number
  offset: PuzzlePoint
}

interface PuzzleCaptchaProps {
  onSolved: () => void
}

const MAX_PIXEL_RATIO = 3
const LOCKED_PHASES: ReadonlySet<PuzzlePhase> = new Set(["loading", "placed"])
const KEYBOARD_HINT_PHASES: ReadonlySet<PuzzlePhase> = new Set(["idle", "dragging"])
const CONFIRM_KEYS: ReadonlySet<string> = new Set(["Enter", " "])
const SETTLE_TRANSITION = "transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1)"

const HINT = { key: "captcha-puzzle-hint", fallback: "Drag the piece into the gap" } as const
const KEYBOARD_HINT = { key: "captcha-puzzle-keyboard-hint", fallback: "Arrow keys move, Enter places" } as const

interface PhaseView {
  status: { key: string; fallback: string; icon: typeof Hand; className: string }
  board: string
  piece: string
}

const PHASE_VIEW: Record<PuzzlePhase, PhaseView> = {
  loading: {
    status: { ...HINT, icon: Hand, className: "text-gray-400" },
    board: "motion-safe:animate-pulse",
    piece: "pointer-events-none opacity-0"
  },
  idle: { status: { ...HINT, icon: Hand, className: "text-gray-400" }, board: "", piece: "cursor-grab" },
  dragging: { status: { ...HINT, icon: Hand, className: "text-gray-400" }, board: "", piece: "cursor-grabbing" },
  near: {
    status: { key: "captcha-puzzle-release", fallback: "Release to place", icon: Magnet, className: "text-yellow-400" },
    board: "",
    piece: "cursor-grabbing"
  },
  miss: {
    status: {
      key: "captcha-puzzle-miss",
      fallback: "Not quite. Try again",
      icon: RotateCcw,
      className: "text-red-400"
    },
    board: "",
    piece: "cursor-grab"
  },
  placed: {
    status: {
      key: "captcha-puzzle-checking",
      fallback: "Checking…",
      icon: LoaderCircle,
      className: "text-gray-300 [&>svg]:motion-safe:animate-spin"
    },
    board: "outline-green-500",
    piece: ""
  }
}

const PIECE_STYLE: React.CSSProperties = {
  width: `${(PIECE_CANVAS_SIZE / BOARD_SIZE) * 100}%`,
  WebkitTouchCallout: "none"
}

const toPieceOffset = (value: number) => `${((value - PIECE_TAB) / PIECE_CANVAS_SIZE) * 100}%`

// translate() percentages resolve against the piece's own width, so the position scales with the board.
const pieceTransform = (point: PuzzlePoint, lifted: boolean) =>
  `translate(${toPieceOffset(point.x)}, ${toPieceOffset(point.y)})${lifted ? " scale(1.06)" : ""}`

const prefersReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches

const prepareCanvas = (canvas: HTMLCanvasElement, size: number) => {
  const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO)
  canvas.width = Math.round(size * ratio)
  canvas.height = Math.round(size * ratio)
  const context = canvas.getContext("2d")
  context?.setTransform(ratio, 0, 0, ratio, 0, 0)
  return context
}

const drawFallbackPicture = (context: CanvasRenderingContext2D) => {
  context.fillStyle = PUZZLE_COLORS.fallbackBase
  context.fillRect(0, 0, BOARD_SIZE, BOARD_SIZE)
  context.lineWidth = 14
  for (let offset = -BOARD_SIZE; offset < BOARD_SIZE * 2; offset += 36) {
    context.strokeStyle = offset % 108 === 0 ? PUZZLE_COLORS.fallbackAccent : PUZZLE_COLORS.fallbackStripe
    context.beginPath()
    context.moveTo(offset, 0)
    context.lineTo(offset - BOARD_SIZE, BOARD_SIZE)
    context.stroke()
  }
}

const drawPicture = (context: CanvasRenderingContext2D, image: HTMLImageElement | null) => {
  if (image) {
    context.drawImage(image, 0, 0, BOARD_SIZE, BOARD_SIZE)
    return
  }
  drawFallbackPicture(context)
}

const drawBoard = (
  context: CanvasRenderingContext2D,
  image: HTMLImageElement | null,
  target: PuzzlePoint,
  active: boolean
) => {
  const gap = new Path2D(piecePath(target))
  context.clearRect(0, 0, BOARD_SIZE, BOARD_SIZE)
  drawPicture(context, image)
  context.fillStyle = PUZZLE_COLORS.gap
  context.fill(gap)
  context.lineWidth = active ? 2.5 : 1.5
  context.strokeStyle = active ? PUZZLE_COLORS.gapOutlineActive : PUZZLE_COLORS.gapOutline
  context.stroke(gap)
}

const drawPiece = (
  context: CanvasRenderingContext2D,
  image: HTMLImageElement | null,
  target: PuzzlePoint,
  active: boolean
) => {
  const outline = new Path2D(piecePath({ x: PIECE_TAB, y: PIECE_TAB }))
  context.clearRect(0, 0, PIECE_CANVAS_SIZE, PIECE_CANVAS_SIZE)
  context.save()
  context.clip(outline)
  context.translate(PIECE_TAB - target.x, PIECE_TAB - target.y)
  drawPicture(context, image)
  context.restore()
  context.lineWidth = active ? 2.5 : 1.5
  context.strokeStyle = active ? PUZZLE_COLORS.gapOutlineActive : PUZZLE_COLORS.pieceOutline
  context.stroke(outline)
}

interface PuzzleCanvases {
  board: HTMLCanvasElement | null
  piece: HTMLCanvasElement | null
}

// resize sets the backing stores to the device pixel ratio, which also clears them; later repaints reuse the contexts.
const paintPuzzle = (
  { board, piece }: PuzzleCanvases,
  image: HTMLImageElement | null,
  target: PuzzlePoint,
  { active = false, resize = false } = {}
) => {
  if (!board || !piece) return false
  const boardContext = resize ? prepareCanvas(board, BOARD_SIZE) : board.getContext("2d")
  const pieceContext = resize ? prepareCanvas(piece, PIECE_CANVAS_SIZE) : piece.getContext("2d")
  if (!boardContext || !pieceContext) return false

  drawBoard(boardContext, image, target, active)
  drawPiece(pieceContext, image, target, active)
  return true
}

const toBoardPoint = (board: HTMLCanvasElement | null, clientX: number, clientY: number): PuzzlePoint | null => {
  const rect = board?.getBoundingClientRect()
  if (!rect || rect.width === 0) return null
  const scale = rect.width / BOARD_SIZE
  return { x: (clientX - rect.left) / scale, y: (clientY - rect.top) / scale }
}

export const PuzzleCaptcha: React.FC<PuzzleCaptchaProps> = ({ onSolved }) => {
  const { t } = useTranslation()
  const [phase, setPhase] = useState<PuzzlePhase>("loading")
  const [{ picture, target }] = useState(() => ({ picture: pickPicture(), target: pickTarget() }))
  const boardRef = useRef<HTMLCanvasElement>(null)
  const pieceRef = useRef<HTMLCanvasElement>(null)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const positionRef = useRef<PuzzlePoint>(PIECE_START)
  const dragRef = useRef<DragState | null>(null)
  const nearRef = useRef(false)
  const [keyboardFocus, setKeyboardFocus] = useState(false)
  const keyboardHintId = useId()

  useEffect(() => {
    let cancelled = false

    const render = (image: HTMLImageElement | null) => {
      if (cancelled) return
      imageRef.current = image
      const canvases = { board: boardRef.current, piece: pieceRef.current }
      if (paintPuzzle(canvases, image, target, { resize: true })) setPhase("idle")
    }

    loadPuzzleImage(picture).then(render, () => {
      render(null)
    })

    return () => {
      cancelled = true
    }
  }, [picture, target])

  // Only called when the piece crosses the snap radius, never per pointer move.
  const redrawOutlines = (active: boolean) => {
    paintPuzzle({ board: boardRef.current, piece: pieceRef.current }, imageRef.current, target, { active })
  }

  const placePiece = (point: PuzzlePoint, { lifted = false, animate = false } = {}) => {
    const piece = pieceRef.current
    if (!piece) return
    positionRef.current = point
    piece.style.transition = animate && !prefersReducedMotion() ? SETTLE_TRANSITION : "none"
    piece.style.transform = pieceTransform(point, lifted)
  }

  const activeDrag = (event: React.PointerEvent) =>
    dragRef.current?.pointerId === event.pointerId ? dragRef.current : null

  const pointerOnBoard = (event: React.PointerEvent) =>
    toBoardPoint(boardRef.current, event.clientX, event.clientY) ?? positionRef.current

  const setNear = (near: boolean) => {
    if (nearRef.current === near) return
    nearRef.current = near
    redrawOutlines(near)
    if (near) WebApp.HapticFeedback.selectionChanged()
    setPhase(near ? "near" : "dragging")
  }

  const returnToStart = () => {
    placePiece(PIECE_START, { animate: true })
    nearRef.current = false
    redrawOutlines(false)
  }

  const handlePointerDown: React.PointerEventHandler<HTMLCanvasElement> = event => {
    if (LOCKED_PHASES.has(phase) || dragRef.current) return
    if (event.pointerType === "mouse" && event.button !== 0) return

    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    const pointer = pointerOnBoard(event)
    const position = positionRef.current
    dragRef.current = { pointerId: event.pointerId, offset: { x: pointer.x - position.x, y: pointer.y - position.y } }
    placePiece(position, { lifted: true })
    WebApp.HapticFeedback.impactOccurred("light")
    setPhase(nearRef.current ? "near" : "dragging")
  }

  // Position is written straight to the element: a drag never re-renders React, only crossing the snap radius does.
  const handlePointerMove: React.PointerEventHandler<HTMLCanvasElement> = event => {
    const drag = activeDrag(event)
    if (!drag) return

    const pointer = pointerOnBoard(event)
    const next = clampPiece({ x: pointer.x - drag.offset.x, y: pointer.y - drag.offset.y })
    placePiece(next, { lifted: true })
    setNear(isWithinSnap(next, target))
  }

  const dropPiece = () => {
    if (isWithinSnap(positionRef.current, target)) {
      placePiece(target, { animate: true })
      WebApp.HapticFeedback.impactOccurred("medium")
      setPhase("placed")
      onSolved()
      return
    }

    returnToStart()
    WebApp.HapticFeedback.notificationOccurred("error")
    setPhase("miss")
  }

  // Arrow keys step the piece through the same clamp and snap as a drag; Enter or Space drops it like a pointer release.
  const handleKeyDown: React.KeyboardEventHandler<HTMLCanvasElement> = event => {
    if (LOCKED_PHASES.has(phase) || dragRef.current) return

    if (CONFIRM_KEYS.has(event.key)) {
      event.preventDefault()
      if (!event.repeat) dropPiece()
      return
    }

    const next = nudgePiece(positionRef.current, event.key, event.shiftKey)
    if (!next) return
    event.preventDefault()
    placePiece(next, { animate: true })
    setNear(isWithinSnap(next, target))
    if (!nearRef.current) setPhase("dragging")
  }

  const handleFocus: React.FocusEventHandler<HTMLCanvasElement> = event => {
    setKeyboardFocus(event.currentTarget.matches(":focus-visible"))
  }

  const handleBlur = () => {
    setKeyboardFocus(false)
  }

  const handlePointerUp: React.PointerEventHandler<HTMLCanvasElement> = event => {
    const drag = activeDrag(event)
    if (!drag) return
    dragRef.current = null
    dropPiece()
  }

  const handlePointerCancel: React.PointerEventHandler<HTMLCanvasElement> = event => {
    const drag = activeDrag(event)
    if (!drag) return
    dragRef.current = null
    returnToStart()
    setPhase("idle")
  }

  const view = PHASE_VIEW[phase]
  const StatusIcon = view.status.icon
  const statusText = keyboardFocus && KEYBOARD_HINT_PHASES.has(phase) ? KEYBOARD_HINT : view.status
  const locked = LOCKED_PHASES.has(phase)

  return (
    <div>
      <div
        className={cn(
          "relative aspect-square w-full touch-none select-none overflow-hidden rounded-xl bg-gray-900 outline outline-2 outline-offset-2 outline-transparent transition-[outline-color] duration-200",
          view.board
        )}>
        <canvas
          className="absolute inset-0 size-full"
          role="img"
          aria-label={t("captcha-puzzle-board", "Picture with a missing piece")}
          ref={boardRef}
        />
        <canvas
          className={cn(
            "absolute left-0 top-0 origin-center drop-shadow-[0_6px_10px_rgba(0,0,0,0.45)] will-change-transform focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-yellow-500",
            view.piece
          )}
          // React never changes this transform after mount; the drag handlers own it from then on.
          style={{ ...PIECE_STYLE, transform: pieceTransform(PIECE_START, false) }}
          role="button"
          tabIndex={locked ? -1 : 0}
          aria-disabled={locked}
          aria-label={t("captcha-puzzle-piece", "Puzzle piece")}
          aria-describedby={keyboardHintId}
          aria-keyshortcuts="ArrowUp ArrowDown ArrowLeft ArrowRight Enter Space"
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onLostPointerCapture={handlePointerCancel}
          ref={pieceRef}
        />
      </div>
      <p
        className={cn("mt-3 flex min-h-5 items-center justify-center gap-1.5 text-sm", view.status.className)}
        role="status">
        <StatusIcon
          aria-hidden="true"
          focusable="false"
          className="size-[1em] shrink-0"
        />
        {t(statusText.key, statusText.fallback)}
      </p>
      <span
        id={keyboardHintId}
        className="sr-only">
        {t(KEYBOARD_HINT.key, KEYBOARD_HINT.fallback)}
      </span>
    </div>
  )
}
